<template>
  <input
    :value="modelValue"
    :step="step || isDecimal ? 'any' : undefined"
    @input="changeEvent"
    @keydown="keyDownEvent"
    @paste="pasteEvent"
    @blur="blurEvent"
  >
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Number, Object],
    validator: (prop: string | number | null) => typeof prop === 'string' || typeof prop === 'number' || prop === null,
    default: '',
  },
  isDecimal: {
    type: Boolean,
    default: true,
  },
  index: {
    type: Number,
    default: 0,
  },
  minValue: {
    type: Number,
    default: 0,
  },
  maxValue: {
    type: Number,
    default: 0,
  },
  step: {
    type: Number,
    default: 0.01,
  },
  allowNull: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['pasteUrl', 'update:modelValue']);

const originalValue = ref('');
const isPaste = ref(false);

const pasteEvent = () => {
  isPaste.value = true;
};

const parseGoogleMapURL = (url: string) => {
  /*
    ex: https://www.google.com/maps/place/56267+Columbia+St,+Calumet+Twp,+MI+49913,+USA/@47.2311146,-88.4537119,17z/data=!3m1!4b1!4m6!3m5!1s0x4d50bb904edf823b:0xf4a101393a2e2f90!8m2!3d47.231111!4d-88.451137!16s%2Fg%2F11t6gjqk5g
  */
  if (url.startsWith('https://www.google.com/maps/place/')) {
    const query = url.split('@');
    if (query.length !== 2) return null;
    const params = query[1].split('!');
    if (params) {
      const geocode1 = params[params.length - 3].split('d');
      const geocode2 = params[params.length - 2].split('d');
      if (geocode1 && geocode2) {
        return {
          lat: geocode1[1],
          lng: geocode2[1],
        };
      }
    }
  }

  return null;
};

const keyDownEvent = (evt: any) => {
  if (evt.keyCode === 32) {
    evt.preventDefault();
  } else {
    originalValue.value = evt.target.value || '';
  }
};

const changeEvent = (evt: any) => {
  const inputValue = evt.target.value;

  if (isPaste.value && props.isDecimal) {
    isPaste.value = false;
    const geoCode = parseGoogleMapURL(inputValue);
    if (geoCode) {
      evt.target.value = props.index === 0 ? geoCode.lat : geoCode.lng;
      emit('update:modelValue', evt.target.value);
      emit('pasteUrl', geoCode);
      return;
    }
  }

  emit('update:modelValue', inputValue);
};

const blurEvent = (evt: any) => {
  const stringValue = evt.target.value.replace(',', '.');
  const value = stringValue ? parseFloat(stringValue) : null;

  // Assign previous value on exit if "null" is not allowed
  if (value === null && !props.allowNull) {
    evt.target.value = originalValue.value;
    return;
  }

  evt.target.value = value;
  emit('update:modelValue', value);
};

onMounted(() => {
  // convert to number if it is a string, or null is not allowed
  if (typeof props.modelValue === 'string' || !props.allowNull) {
    emit('update:modelValue', Number(props.modelValue));
  }

  emit('update:modelValue', props.modelValue);
});
</script>
